import {Action, Selector, State, StateContext} from '@ngxs/store';
import {ProductConfigurationType, ProductListItem} from '../../models/product.model';
import {FormState} from '../../models/form-state.model';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {handleFetchListPage} from '../../helpers/search.helper';
import {ProductService} from '../../services/product.service';
import {ProductActions} from './product.actions';
import {clearFormValidationErrors, setFormValidationErrors} from '../../helpers/form.helper';
import {ProductCpiService} from '../../services/product-cpi.service';
import {ProductPgService} from '../../services/product-pg.service';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {ProductPgActions} from '../product-pg/product-pg.actions';
import {tap} from 'rxjs/operators';

interface ProductStateModel {
    form: FormState;
    list: SearchList<ProductListItem>;
    fieldList: ProductListItem[];
}

@State<ProductStateModel>({
    name: ProductState.STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<ProductListItem>({
            filters: new FormState()
        }),
        fieldList: null,
    }
})

@Injectable()
export class ProductState {

    static readonly STATE_NAME = 'product';

    constructor(
        private navService: NavService,
        private productService: ProductService,
        private productPgService: ProductPgService,
        private productCpiService: ProductCpiService,
    ) {
    }

    @Selector()
    static productList(state) {
        return state.list;
    }

    @Selector()
    static productFieldList(state) {
        return state.fieldList;
    }

    @Selector()
    static formErrors(state) {
        return state.form.validationErrors;
    }

    @Action(ProductActions.GetListPage)
    getListPage(ctx: StateContext<ProductStateModel>, action: ProductActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, props => this.productService.fetchList(props));
    }

    @Action(ProductActions.Add)
    add(ctx: StateContext<ProductStateModel>, action: ProductActions.Add) {
        let httpCall = null;
        let navCode = null;
        switch (action.configurationType) {
            case ProductConfigurationType.CPI:
                httpCall = this.productCpiService.add(action.data);
                navCode = NavItemCodes.PRODUCT_CPI_EDIT;
                break;
            case ProductConfigurationType.PG:
                httpCall = this.productPgService.add(action.data);
                navCode = NavItemCodes.PRODUCT_PG_EDIT;
                break;
        }
        if (httpCall === null) {
            return;
        }
        return httpCall.subscribe(
            (product) => this.navService.navigateByCode(navCode, {productId: product.id}),
            ({error}) => ctx.dispatch(new ProductActions.FormFailure(error))
        );
    }

    @Action(ProductActions.FieldList)
    getProductFieldList(ctx: StateContext<ProductStateModel>, {params}: ProductActions.FieldList) {
        return this.productService.getFieldList(params).pipe(
            tap((items: ProductListItem[]) => {
                ctx.patchState({
                    fieldList: items.map(item => new ProductListItem(item))
                });
            })
        );
    }

    @Action(ProductActions.FormFailure)
    addProductFailure(ctx: StateContext<ProductStateModel>, {validationErrors = []}: ProductActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, ProductState.STATE_NAME);
    }

    @Action(ProductActions.ClearValidationErrors)
    productClearValidationErrors(ctx: StateContext<ProductStateModel>, {fields = []}: ProductActions.ClearValidationErrors) {
        clearFormValidationErrors(ctx, fields);
    }
}
